/**
 * Which sphere-watch this console talks to.
 *
 * THE CONSOLE AND THE API ARE ON DIFFERENT HOSTS. The console is static files on
 * Firebase Hosting; the API is Express on the watch VPS behind nginx. So the API
 * base is never the page's own origin -- an earlier version derived it that way,
 * which cannot work: Firebase rewrites `**` to /index.html, so every /api/v1 call
 * would have been answered with the HTML shell and died parsing JSON.
 *
 * Cross-origin means the API must allow this origin: CONSOLE_ORIGINS on the
 * service is the other half of every entry below, and a mismatch there is a CORS
 * failure in the browser rather than anything visible in the service log.
 *
 * SELECTION IS BY HOSTNAME, with the same safety property as the CRM console's
 * src/config/bases.ts: anything that is not EXACTLY the production hostname
 * resolves to UAT. A console that guessed "production" for an unknown host would
 * let a developer acknowledge a real incident from a preview build.
 *
 * The unknown-host case resolves to UAT'S API, not to localhost. That
 * distinction is the whole reason this file was rewritten: the previous fallback
 * sent every non-production host to `http://localhost:4300`, so a build served
 * from a Firebase site or preview channel called plain HTTP from an HTTPS page
 * and was blocked as mixed content before it could even fail to connect. Every
 * screen showed a network error and nothing said why. localhost is now reached
 * only from an actual localhost.
 */
export type WatchEnv = 'prod' | 'uat';

export interface Bases {
  apiRoot: string;
  /**
   * The password sign-in (routes/signin.js): beside /api/v1 on the same host,
   * because nobody calling it is signed in yet and everything on /api/v1 is
   * behind the staff check.
   */
  authRoot: string;
  env: WatchEnv;
  firebaseProject: string;
}

/**
 * One row per environment, and the ONE place to correct an address.
 *
 * `api` is a DEDICATED HOST, deliberately not `backend.limosphere.co/watch` the
 * way sphere-crm is served. That address belongs to the VPS running sphere-crm
 * and dispatch-be — the box this service exists to watch — so routing the
 * monitoring API through it would put the incident board behind the nginx of the
 * machine whose failure you most need to read about. `watch-api` resolves
 * straight to the watch VPS instead, and the console stays loadable when the
 * monitored box is not.
 *
 * The side benefit is that there is no path prefix to get wrong: no nginx
 * trailing-slash subtlety, and PUBLIC_BASE_URL on the service is just this
 * value.
 */
const ENVIRONMENTS: Record<WatchEnv, { consoleHost: string; api: string; firebaseProject: string }> = {
  prod: {
    consoleHost: 'watch.limosphere.com',
    api: 'https://watch-api.limosphere.com',
    firebaseProject: 'my-dispatcher-us',
  },
  // Not deployed yet — production first. Until it is, an unknown host resolves
  // here and fails on DNS, which is the correct direction: a preview build must
  // fail rather than quietly reach production.
  uat: {
    consoleHost: 'watch-uat.limosphere.com',
    api: 'https://watch-api-uat.limosphere.com',
    firebaseProject: 'dispatch-system-44a1f',
  },
};

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export function deriveBases(hostname: string, override?: string): Bases {
  const isProd = hostname === ENVIRONMENTS.prod.consoleHost;
  const environment = isProd ? ENVIRONMENTS.prod : ENVIRONMENTS.uat;

  // A real localhost gets the local service; nothing else ever does.
  const base = override || (LOCAL_HOSTS.has(hostname) ? 'http://localhost:4300' : environment.api);

  return {
    apiRoot: `${base.replace(/\/$/, '')}/api/v1`,
    authRoot: `${base.replace(/\/$/, '')}/api/auth`,
    env: isProd ? 'prod' : 'uat',
    firebaseProject: environment.firebaseProject,
  };
}

export const config = deriveBases(
  window.location.hostname,
  // Escape hatch for pointing a local console at a deployed service. Not used by
  // either deploy script on purpose: the addresses above are the single source
  // of truth, and a build-time variable set in two shell scripts is a second
  // place for them to drift.
  import.meta.env.VITE_WATCH_BASE as string | undefined,
);
